/* MagicLinkConsumePage.jsx — Silent exchange of a magic-link token for
 * an authenticated session, then redirect into operational context.
 *
 * UX: short reassuring loading state ("Returning you to your Deal Room…")
 * then automatic navigation. If the token is expired/used/invalid, show
 * a calm operational fallback with a one-tap "Request a new link" CTA —
 * never blame the user, never expose token details.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  ArrowRight,
  Send,
  Lock,
} from "lucide-react";

import { api, apiError } from "../../lib/api";
import { useAuth } from "../../data/authStore";

export default function MagicLinkConsumePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const dealIdHint = params.get("deal_id");
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("loading"); // loading | error
  const [errorMsg, setErrorMsg] = useState("");
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!token) {
      setErrorMsg("This access link is missing a token.");
      setState("error");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/api/auth/magic/consume", { token });
        // Refresh the auth store so the Sidebar / role-gated routes
        // recognise the new session immediately.
        await refresh?.();
        const dest = data?.redirect_to || (dealIdHint ? `/deal-rooms/${dealIdHint}` : "/today");
        nav(dest, { replace: true });
      } catch (err) {
        setErrorMsg(apiError(err) || "This access link is no longer valid.");
        setState("error");
      }
    })();
  }, [token, dealIdHint, nav, refresh]);

  return (
    <Shell>
      {state === "loading" ? (
        <div className="gp-card p-8 text-center" data-testid="magic-link-consume-loading">
          <Loader2
            size={26}
            className="mx-auto animate-spin"
            style={{ color: "var(--gp-violet-deep)" }}
          />
          <p
            className="mt-4 text-[14px] font-semibold"
            style={{ color: "var(--gp-text)" }}
          >
            {dealIdHint ? "Returning you to your Deal Room…" : "Signing you in…"}
          </p>
          <p
            className="mt-1.5 text-[12px]"
            style={{ color: "var(--gp-text-subtle)" }}
          >
            Restoring your secure session
          </p>
        </div>
      ) : (
        <div className="gp-card p-6 sm:p-7" data-testid="magic-link-consume-error">
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: "var(--gp-rose-soft)",
                border: "1px solid var(--gp-rose-border)",
                color: "#B91C1C",
              }}
            >
              <AlertTriangle size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="gp-eyebrow mb-1.5" style={{ color: "#B91C1C" }}>
                ACCESS LINK · EXPIRED OR USED
              </div>
              <h1
                className="font-display font-bold text-[20px] sm:text-[22px] tracking-tight"
                style={{ color: "var(--gp-text)" }}
              >
                This link is no longer valid
              </h1>
              <p
                className="mt-2 text-[13px] sm:text-[13.5px] leading-snug"
                style={{ color: "var(--gp-text-muted)" }}
                data-testid="magic-link-consume-error-msg"
              >
                {errorMsg}
                {" "}For your security, secure access links expire after 15 minutes and can only be used once. Request a fresh one and we'll send it in seconds.
              </p>
              <Link
                to={dealIdHint ? `/access?deal_id=${dealIdHint}` : "/access"}
                className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl text-white text-[13.5px] font-bold gp-btn-primary w-full sm:w-auto"
                data-testid="magic-link-consume-request-new"
              >
                Email me a fresh link <ArrowRight size={14} />
              </Link>
              <p
                className="mt-3 text-[11.5px] flex items-center gap-1.5"
                style={{ color: "var(--gp-text-subtle)" }}
              >
                <Lock size={11} /> Or{" "}
                <Link to="/login" className="font-semibold" style={{ color: "var(--gp-violet-deep)" }} data-testid="magic-link-consume-to-login">
                  sign in with a password
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

/* ─── Shell — calm centred layout, no nav, ambient halo ──────────────── */
const Shell = ({ children }) => (
  <div className="min-h-screen gp-surface" data-testid="magic-link-consume-page">
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full"
      style={{
        background: "radial-gradient(circle, rgba(124,58,237,0.10), transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <div className="relative mx-auto max-w-[460px] px-4 sm:px-6 pt-6 sm:pt-12 pb-10 space-y-4">
      <header className="flex items-center justify-center mb-2">
        <span className="inline-flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
            }}
          >
            <Send size={13} className="text-white" />
          </div>
          <span className="font-display font-bold text-[14px] tracking-tight" style={{ color: "var(--gp-text)" }}>
            ChainFlow <span style={{ color: "var(--gp-text-subtle)" }}>OS</span>
          </span>
        </span>
      </header>
      {children}
    </div>
  </div>
);
